import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { OktaService } from './okta.service';
import { environment } from '../../../environments/environment';
import { HcToastOptions, HcToasterService } from '@healthcatalyst/cashmere';

export interface AppElement {
    'appID': number,
    'name': string,
    'references': number
}

@Injectable()
export class AppService {
    appList:BehaviorSubject<AppElement[] | null> = new BehaviorSubject<AppElement[] | null>(null);
    loadFailed = false;

    constructor( private http: HttpClient, private oktaService: OktaService, private toasterService: HcToasterService ){};

    get appsUpdated(): Observable<AppElement[] | null> {
        return this.appList.asObservable();
    }

    refresh() {
        this.loadFailed = false;

        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = {headers: headers};

        this.http.get<any>( environment.azure.url + '/admin.apps', options)
            .subscribe({
                next: data => {
                    let loadedApps: AppElement[] = [];
                    data.forEach((app:any) => {
                        loadedApps.push({
                            appID: app.appID,
                            name: app.name,
                            references: app.references
                        });
                    });
                    this.appList.next ( loadedApps );
                    this.loadFailed = false;
                },
                error: msg => { 
                    this.loadFailed = true;
                    this.appList.next(null);
                    console.log( msg );
                }
            });
    }

    add( target: AppElement ) {
        // Write target to database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = {headers: headers};

        let postSub = this.http.post<any>( environment.azure.url + '/admin.apps.add',
            {
                name: target.name
            },
            options
        )
        .subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'App Added',
                    body: target.name + " successfully added.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Add Failed',
                    body: "Unable to add app. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log( msg );
                postSub.unsubscribe();
            }
        });
    }

    remove( target: AppElement ) {
        // Remove target from database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = {headers: headers};

        let postSub = this.http.post<any>( environment.azure.url + '/admin.apps.remove',
            {
                appID: target.appID
            },
            options
        )
        .subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'App Removed',
                    body: target.name + " successfully removed.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Remove Failed',
                    body: "Unable to remove app. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log( msg );
                postSub.unsubscribe();
            }
        });
    }

    update( target: AppElement ) {
        // Update target in database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = {headers: headers};

        let postSub = this.http.post<any>( environment.azure.url + '/admin.apps.update',
            {
                appID: target.appID,
                name: target.name
            },
            options
        )
        .subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'App Updated',
                    body: target.name + " successfully updated.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Update Failed',
                    body: "Unable to update app. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log( msg );
                postSub.unsubscribe();
            }
        });
    }
}
